import { useState, useEffect, useMemo } from "react";
import "./index.css";
import {
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import rtlPlugin from "stylis-plugin-rtl";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";
import { ToastContainer } from "react-toastify";

import theme from "assets/theme";
import themeDark from "assets/theme-dark";
import themeRTL from "assets/theme/theme-rtl";
import themeDarkRTL from "assets/theme-dark/theme-rtl";

import Sidenav from "examples/Sidenav";
import Configurator from "examples/Configurator";
import MDBox from "components/MDBox";

import { routes, routesUser, routesPOS, routesManager } from "routes";
import {
  useMaterialUIController,
  setMiniSidenav,
  setOpenConfigurator,
} from "context";

import brandWhite from "assets/images/logo-ct.png";
import brandDark from "assets/images/logo-ct-dark.png";
import "../src/assets/custome.css";

export default function App() {
  const [controller, dispatch] = useMaterialUIController();
  const {
    miniSidenav,
    direction,
    layout,
    openConfigurator,
    sidenavColor,
    transparentSidenav,
    whiteSidenav,
    darkMode,
  } = controller;

  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const [rtlCache, setRtlCache] = useState(null);
  const [usertype, setUsertype] = useState(null); // ✅ initially null, not 0
  const [isLoading, setIsLoading] = useState(true);
  const { pathname } = useLocation();
  const navigate = useNavigate();

  // Setup RTL cache
  useMemo(() => {
    const cacheRtl = createCache({
      key: "rtl",
      stylisPlugins: [rtlPlugin],
    });
    setRtlCache(cacheRtl);
  }, []);

  // Load usertype and token
  // useEffect(() => {
  //   document.body.setAttribute("dir", direction);

  //   const token = localStorage.getItem("token");
  //   const role_id = localStorage.getItem("role_id");

  //   if (!token) {
  //     console.warn("🚫 No token found — redirecting to login");
  //     navigate("/auth/login");
  //     return;
  //   }

  //   const parsedRole = parseInt(role_id);
  //   console.log("🧠 Loaded from localStorage:", {
  //     token,
  //     rawRole: role_id,
  //     parsedRole,
  //   });

  //   setUsertype(parsedRole);
  //   setIsLoading(false);
  // }, [direction]);

  useEffect(() => {
    document.body.setAttribute("dir", direction);

    const token = localStorage.getItem("token");
    const role_id = localStorage.getItem("role_id");

    if (!token) {
      console.warn("🚫 No token found — redirecting to login");
      setIsLoading(false); // ✅ stop loading before redirect
      navigate("/auth/login", { replace: true });
      return;
    }

    const parsedRole = parseInt(role_id);
    console.log("🧠 Loaded from localStorage:", {
      token,
      rawRole: role_id,
      parsedRole,
    });

    setUsertype(parsedRole);
    setIsLoading(false);
  }, [direction, navigate]);

  // Scroll to top when route changes
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  // Route selector based on role
  const getCurrentRoutes = () => {
    // console.log("🔍 Checking usertype:", usertype);
    if (usertype === 1) {
      // console.log("✅ Admin detected — loading routes");
      return routes;
    }
    if (usertype === 2) {
      // console.log("✅ Normal user detected — loading routesUser");
      return routesUser;
    }
    if (usertype === 4) {
      // console.log("✅ POS user detected — loading routesPOS");
      return routesPOS;
    }
    if (usertype === 5) {
      // console.log("✅ For manager");
      return routesManager;
    }
    console.warn("⚠️ Unknown role — defaulting to routesUser");
    return routesUser;
  };

  // Map routes recursively
  const getRoutes = (allRoutes) =>
    allRoutes.map((route) => {
      if (route.collapse) {
        return getRoutes(route.collapse);
      }
      if (route.route) {
        return (
          <Route
            key={route.key || route.route} // ✅ unique key fix
            path={route.route}
            element={route.component}
          />
        );
      }
      return null;
    });

  // Handle hover open/close of sidenav
  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };
  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };
  const handleConfiguratorOpen = () =>
    setOpenConfigurator(dispatch, !openConfigurator);

  // Wait until usertype is loaded
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen text-lg font-semibold">
        Loading...
      </div>
    );
  }

  return (
    <ThemeProvider theme={darkMode ? themeDark : theme}>
      <ToastContainer position="top-right" autoClose={3000} />
      <CssBaseline />

      {/* {layout === "dashboard" && ( */}
      {layout === "dashboard" && usertype !== 2 && usertype !== 5 && (
        <Sidenav
          color={sidenavColor}
          brand={
            (transparentSidenav && !darkMode) || whiteSidenav
              ? brandDark
              : brandWhite
          }
          brandName="JTSERP"
          routes={getCurrentRoutes()}
          onMouseEnter={handleOnMouseEnter}
          onMouseLeave={handleOnMouseLeave}
        />
      )}

      {layout === "vr" && <Configurator />}

      <Routes>
        {getRoutes(getCurrentRoutes())}
        {usertype === 4 && <Route path="*" element={<Navigate to="/pos" />} />}
      </Routes>
    </ThemeProvider>
  );
}
